import React from 'react';
import styled from '@emotion/styled';
import { Project } from './types'; // Adjust import path
import { ProjectStateDisplay } from './ProjectStateDisplay';
import { DeadlineDisplay, getDeadlineLevel } from './DeadlineDisplay';
import { Buttons } from '../../toolympus/components/primitives';
import { useStatusDates } from '../Settings/useStatusDates';
import { ThemeSettings } from '../../theme';
import { MembersDisplay } from './MembersDisplay';
import { SupportDisplay } from './SupportDisplay';
import { Typography } from '@mui/material';


interface Props {
  items: Project[];
  startEditing: (p: Project) => void;
  startEditingStatusCurrent: (p: Project) => void;
  startEditingStatusPrev: (p: Project) => void;
  showStages: boolean;
  overloadedMembers: string[];
  mode: "project" | "process";
}

export const formatMembers = (members?: string[]) => {
  return members?.join(', ') || '-';
};

const getProjectStagesDeadlineLevel = (project: Project) => {
  const unfinishedStages = project.stages?.filter(s => s.state !== "completed") || [];
  return unfinishedStages.reduce<string>((r,s) => {
    const sLevel = getDeadlineLevel(s.deadline);
    if(sLevel !== "ok") {
      if(sLevel === "danger") {
        return "danger";
      } else if(r !== "danger") {
        return sLevel;
      }
    }
    return r;
  }, "ok");
}

export const ProjectList = (props: Props) => {
  const { items, showStages } = props;

  const statusDates = useStatusDates(props.mode);

  return (
    <ProjectListContainer>
      {items.map((project) => {
        const deadlineLevel = getProjectStagesDeadlineLevel(project);
        return (
          <ProjectItem key={project._id} onClick={() => props.startEditing(project)}>
            <ProjectHeader>
              <ProjectField className="front-column">
                <CodeWithState>
                  <FieldLabel>{project.code || '-'}</FieldLabel>
                  <FieldValue>{project.curator || '-'}</FieldValue>
                  <ProjectStateDisplay value={project.state} />
                </CodeWithState>
              </ProjectField>

              <ProjectHeaderInnerFields>
                <ProjectField className="title full-width">
                  <FieldValue className="larger">{project.title || '-'}</FieldValue>
                </ProjectField>
                
                <ProjectField>
                  <FieldLabel>ПГ + Эксперты</FieldLabel>
                  <FieldValue><MembersDisplay members={project.members} warning={props.overloadedMembers} />
                  {!!project.members?.length && " "}
                  {!!project.experts?.length && "+ "}
                  <MembersDisplay members={project.experts} realEmpty /></FieldValue>
                </ProjectField>
                <ProjectField>
                  <FieldLabel>Главные герои</FieldLabel>
                  <FieldValue>
                    <SupportDisplay value={project.support_needed} condensed />{!!project.support_needed?.length && !!project.heroes?.length && " "}
                    <MembersDisplay members={project.heroes} warning={props.overloadedMembers} realEmpty={!!project.support_needed?.length} />
                  </FieldValue>
                </ProjectField>

                <ProjectField>
                  <FieldLabel>Дедлайн</FieldLabel>
                  <FieldValue><DeadlineDisplay value={project.deadline} noRedZone={project.state === "completed"} /></FieldValue>
                </ProjectField>

                {!showStages && deadlineLevel === "danger" &&
                  <ProjectNote level={deadlineLevel} className="full-width">
                    есть просроченные дедлайны
                  </ProjectNote>}

                {!showStages && deadlineLevel === "warning" &&
                  <ProjectNote level={deadlineLevel} className="full-width">
                    приближаются дедлайны
                  </ProjectNote>}

                {!!showStages && project.stages && project.stages.length > 0 && (
                  <StageList className="full-width">
                    <StageItem>
                        <ProjectField>
                          <FieldLabel>промежуточные дедлайны</FieldLabel>
                        </ProjectField>
                        
                    </StageItem>
                    {project.stages.map(stage => (
                      <StageItem key={stage._id}>
                        <ProjectField>
                          <FieldValue>
                            <Buttons>
                              <ProjectStateDisplay value={stage.state} condensed forStage />
                              {stage.title || '-'}
                            </Buttons>
                          </FieldValue>
                        </ProjectField>
                        <ProjectField>
                          <FieldValue><DeadlineDisplay value={stage.deadline} noRedZone={stage.state === "completed"} /></FieldValue>
                          {!!stage.members?.length && <FieldValue>{formatMembers(stage.members)}</FieldValue>}
                        </ProjectField>
                        
                      </StageItem>
                    ))}
                  </StageList>
                )}
              </ProjectHeaderInnerFields>

              <ProjectStatusField
                className="semi-backed"
                alert={project.project_status?.alert}
                onClick={e => {
                  e.stopPropagation();
                  props.startEditingStatusCurrent(project);
                }}>
                <FieldLabel underscore>Задачи {statusDates.dates.current.label}</FieldLabel>
                <FieldValue className="description">
                  {project.project_status?.description || '-'}
                </FieldValue>
              </ProjectStatusField>


              <ProjectStatusField
                className="backed"
                alert={project.project_status_prev?.alert}
                onClick={e => {
                  e.stopPropagation();
                  props.startEditingStatusPrev(project);
                }}>
                <FieldLabel>Задачи {statusDates.dates.prev.label}</FieldLabel>
                <FieldValue className="description">
                  {project.project_status_prev?.description || '-'}
                </FieldValue>
              </ProjectStatusField>

              
            </ProjectHeader>

            
          </ProjectItem>
      )})}
    </ProjectListContainer>
  );
};



const ProjectListContainer = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  height: 100%;
  min-height: 100%;
  overflow: auto;
  line-height: 1.25;
`;

const ProjectItem = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #fffffff0;

  width: 100%;
  cursor: pointer;
`;

const ProjectHeader = styled.div`
  display: grid;
  grid-template-columns: 100px 500px 1fr 1fr;
  gap: 16px;
  align-items: start;

  ${props => props.theme.breakpoints.down("sm")} {
    grid-template-columns: 1fr;
  }
`;

// grid-template-columns: ${props => props.variant === "condensed" ? "2fr 1fr 140px" : "1fr 1fr 1fr 140px"};

const ProjectHeaderInnerFields = styled.div<{ variant?: "condensed" }>`
  display: grid;
  grid-template-columns: 2fr 1fr 140px;
  gap: 16px;
  row-gap: 8px;
  align-items: start;

  & .title {
    grid-column: 1 / span 3;
    width: 100%;
  }
  & .full-width {
    grid-column: 1 / span 3;
    width: 100%;
  }
`;



export const FieldLabel = styled.span<{ underscore?: boolean }>`
  font-size: 9px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${props => props.underscore ? ThemeSettings.colors.primary : "transparent"};
  width: auto;
  align-self: flex-start;
`;

export const FieldValue = styled.span`
  font-size: 14px;
  &.description {
      white-space: pre-line;
  }

  &.larger {
    font-size: 1rem;
    font-weight: 500;
  }
`;


export const ProjectField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

export const ProjectStatusField = styled(ProjectField)<{ alert?: boolean }>`
  position: relative;
  height: 100%;

  & ${FieldLabel}, ${FieldValue} {
    position: relative;
  }

  &:after {
    position: absolute;
    content: '';
    display: ${props => props.alert ? "block" : "none"};
    width: 12px;
    height: 12px;
    border-radius: 6px;
    background: ${ThemeSettings.colors.danger};

    left: -18px;
    top: 17px;
  }

  &.backed, &.semi-backed {
    padding-left: 16px;
    ${props => props.theme.breakpoints.down("sm")} {
      padding-left: 0;
    }

    &:before {
      background: linear-gradient(#b9d1ef60, #b9d1ef60);
      opacity: 0.25;
      border-left: 1px solid ${ThemeSettings.colors.blue_grey_dark};
      position: absolute;
      content: '';
      top: -16px;
      left: 0px;
      width: calc(100% + 16px);
      height: calc(100% + 16px + 16px);
      border-radius: 0 8px 8px 0;

      ${props => props.theme.breakpoints.down("sm")} {
        top: -8px;
        left: -16px;
        width: calc(100% + 16px + 16px);
        border-left: none;
        border-top: 1px solid ${ThemeSettings.colors.blue_grey_dark};
        border-radius: 0;
      }
    }
    &:after {
      left: -2px;

      ${props => props.theme.breakpoints.down("sm")} {
        left: -14px;
      }
    }
  }
  &.semi-backed {
    &:before {
      background: transparent;
    }
  }
`;

const ProjectNote = styled(Typography)<{ level?: "danger" | "warning" }>`
  color: ${props => (ThemeSettings.colors as any)[props.level || "ok"] || ThemeSettings.colors.blue_grey_dark};
`;
ProjectNote.defaultProps = { variant: "caption", component: "p", color: "textSecondary" } as any;


const CodeWithState = styled.div`
  display: flex;
  flex-flow: column;
  gap: 4px;
  align-items: flex-start;

  
  ${props => props.theme.breakpoints.down("sm")} {
    flex-flow: row;
    align-items: center;

    & > :first-child {
      flex-grow: 1;
    }
  }
`;

const StageList = styled.div`
  /* margin-top: 8px; */

  ${props => props.theme.breakpoints.down("sm")} {
    display: none;
  }
`;

const StageItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px;
  gap: 16px;
  padding: 12px 0;
  font-size: 13px;
  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;
